import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Tag,
  Plus,
  Check,
  X,
  Search,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Archive,
  RotateCcw,
  ImagePlus,
  Loader2,
  Trash2,
  Pencil,
} from 'lucide-react';
import { cn } from '@lib/utils';
import { catalogService } from '@services/catalog.service';
import type { Category } from '@apptypes/index';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';

// ── Category icons ────────────────────────────────────────────────────────────

/**
 * SVG first: these are line art, and a vector stays sharp at any tile size.
 * PNG and WebP are accepted because not every icon arrives as a vector.
 */
const ACCEPTED_ICON_TYPES = ['image/svg+xml', 'image/png', 'image/webp'];

/** 512KB is generous for an icon and mean enough to stop a 4MB photograph. */
const MAX_ICON_BYTES = 512 * 1024;

type IconVariant = 'light' | 'dark';

async function uploadIcon(file: File): Promise<string> {
  if (!ACCEPTED_ICON_TYPES.includes(file.type)) {
    throw new Error('Use an SVG, PNG or WebP.');
  }
  if (file.size > MAX_ICON_BYTES) {
    throw new Error('Too large. An icon should be under 512KB.');
  }
  return catalogService.uploadCategoryIcon(file);
}

/**
 * One icon slot: the preview, on the background it will actually appear on,
 * with its own upload and remove controls.
 *
 * Previewing the dark icon on a dark tile is the point. It is the only way to
 * notice that a dark grey glyph is invisible in dark mode before a shopper
 * does, and no amount of describing it in a hint achieves the same thing.
 */
function IconField({
  variant,
  url,
  onPick,
  onRemove,
  busy,
}: {
  variant: IconVariant;
  url?: string | null;
  onPick: (file: File) => void;
  onRemove: () => void;
  busy: boolean;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const dark = variant === 'dark';

  return (
    <div className="flex-1">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
        {dark ? 'Dark mode' : 'Light mode'}
      </div>
      <div
        className={cn(
          'flex items-center gap-3 rounded-2xl border p-3',
          dark
            ? 'border-[var(--border-strong)] bg-[#11170F]'
            : 'border-[var(--border-subtle)] bg-[var(--bg-base)]',
        )}
      >
        <span
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl',
            dark ? 'bg-black/25' : 'bg-white',
          )}
        >
          {url ? (
            // contain, never cover: an icon is drawn inside its tile, and
            // cropping one to fill the square cuts its edges off.
            <img src={url} alt="" className="h-full w-full object-contain p-1.5" />
          ) : (
            <Tag
              className={cn('h-4 w-4', dark ? 'text-white/40' : 'text-[var(--text-muted)]')}
              strokeWidth={1.5}
            />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED_ICON_TYPES.join(',')}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onPick(file);
              e.target.value = '';
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all disabled:opacity-50',
                dark
                  ? 'bg-white/10 text-white hover:bg-white/20'
                  : 'border border-[var(--border-default)] bg-white text-[var(--text-primary)] hover:border-[var(--border-strong)]',
              )}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
              ) : (
                <ImagePlus className="h-3 w-3" strokeWidth={1.5} />
              )}
              {url ? 'Replace' : 'Upload'}
            </button>
            {url && (
              <button
                type="button"
                disabled={busy}
                onClick={onRemove}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all disabled:opacity-50',
                  dark
                    ? 'text-white/70 hover:text-white'
                    : 'text-[var(--state-danger)] hover:brightness-110',
                )}
              >
                <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                Remove
              </button>
            )}
          </div>
          {!url && (
            <p
              className={cn(
                'mt-1 text-[11px]',
                dark ? 'text-white/45' : 'text-[var(--text-muted)]',
              )}
            >
              {dark ? 'Falls back to the light icon' : 'Falls back to a drawn icon'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Both slots, side by side, with the guidance that applies to both. */
function IconFields({
  light,
  dark,
  onChange,
}: {
  light?: string | null;
  dark?: string | null;
  onChange: (variant: IconVariant, url: string) => Promise<void> | void;
}) {
  const [busy, setBusy] = useState<IconVariant | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (variant: IconVariant, file: File) => {
    setBusy(variant);
    setError(null);
    try {
      await onChange(variant, await uploadIcon(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (variant: IconVariant) => {
    setBusy(variant);
    setError(null);
    try {
      await onChange(variant, '');
    } catch {
      setError('Could not remove it. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <IconField
          variant="light"
          url={light}
          busy={busy === 'light'}
          onPick={(f) => pick('light', f)}
          onRemove={() => remove('light')}
        />
        <IconField
          variant="dark"
          url={dark}
          busy={busy === 'dark'}
          onPick={(f) => pick('dark', f)}
          onRemove={() => remove('dark')}
        />
      </div>
      <p className="mt-2 text-[12px] text-[var(--text-muted)]">
        A square SVG, or a PNG on a transparent background, under 512KB. Both are
        optional.
      </p>
      {error && <p className="mt-1 text-[12px] text-[var(--state-danger)]">{error}</p>}
    </div>
  );
}

/** The row control: one tile showing what a shopper sees, and a way in. */
function CategoryIconButton({ category }: { category: Category }) {
  const [editing, setEditing] = useState(false);
  const shown = category.imageUrl ?? category.darkImageUrl;

  const tile = (
    <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-base)]">
      {shown ? (
        <img src={shown} alt="" className="h-full w-full object-contain p-1.5" />
      ) : (
        <Tag className="h-4 w-4 text-[var(--text-muted)]" strokeWidth={1.5} />
      )}
    </span>
  );

  return (
    <>
      <PermissionGate permission="categories.update" fallback={tile}>
        <button
          type="button"
          title={shown ? 'Change the icons' : 'Add icons'}
          onClick={() => setEditing(true)}
          className="group relative shrink-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-leaf)]"
        >
          {tile}
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
            <Pencil className="h-3.5 w-3.5 text-white" strokeWidth={1.5} />
          </span>
          {/* A category with a light icon but no dark one is easy to miss, and
              it is the case that looks broken in dark mode. */}
          {category.imageUrl && !category.darkImageUrl && (
            <span
              title="No dark-mode icon"
              className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[var(--state-warning)] ring-2 ring-[var(--bg-surface)]"
            />
          )}
        </button>
      </PermissionGate>
      {editing && (
        <CategoryIconsModal category={category} onClose={() => setEditing(false)} />
      )}
    </>
  );
}

/** Editing the icons of a category that already exists. Saves as you go. */
function CategoryIconsModal({
  category,
  onClose,
}: {
  category: Category;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const save = async (variant: IconVariant, url: string) => {
    await catalogService.updateCategory(
      category.id,
      variant === 'dark' ? { darkImageUrl: url } : { imageUrl: url },
    );
    await queryClient.invalidateQueries({ queryKey: ['categories'] });
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="w-full max-w-xl rounded-3xl bg-white p-8 shadow-lift">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-[18px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            Icons for {category.name}
          </h2>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
        <p className="mb-6 text-[13px] text-[var(--text-muted)]">
          What shoppers see beside this category in the app and on the website.
          Changes save as you make them.
        </p>

        <IconFields
          light={category.imageUrl}
          dark={category.darkImageUrl}
          onChange={save}
        />

        <div className="mt-8 flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 active:brightness-95"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: Category['status'] }) {
  const map = {
    approved: {
      label: 'Approved',
      icon: CheckCircle2,
      cls: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
    },
    pending_approval: {
      label: 'Pending',
      icon: Clock,
      cls: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
    },
    rejected: {
      label: 'Rejected',
      icon: XCircle,
      cls: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
    },
    archived: {
      label: 'Archived',
      icon: XCircle,
      cls: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
    },
  } as const;
  const cfg = map[status] ?? map.archived;
  const Icon = cfg.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold',
        cfg.cls,
      )}
    >
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {cfg.label}
    </span>
  );
}

// ── Create modal ──────────────────────────────────────────────────────────────

function CreateCategoryModal({
  categories,
  onClose,
}: {
  categories: Category[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState('');
  // Uploaded as they are picked, held here until the category exists. An
  // abandoned Create leaves an orphan object in S3, which costs nothing and is
  // far better than making staff come back to add an icon afterwards.
  const [imageUrl, setImageUrl] = useState('');
  const [darkImageUrl, setDarkImageUrl] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      catalogService.createCategory({
        name: name.trim(),
        description: description.trim() || undefined,
        parentId: parentId || undefined,
        imageUrl: imageUrl || undefined,
        darkImageUrl: darkImageUrl || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
  });

  // Only approved categories can be parents
  const parentOptions = categories.filter((c) => c.status === 'approved');

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-xl p-8 max-h-[90vh] overflow-y-auto">
        {/* Modal header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[18px] font-semibold text-[var(--text-primary)] tracking-[-0.01em]">
            Create Category
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Electronics"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this category"
              rows={3}
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
            />
          </div>

          {parentOptions.length > 0 && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
                Parent Category
              </label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
              >
                <option value="">None (top-level)</option>
                {parentOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Icons
            </label>
            <IconFields
              light={imageUrl}
              dark={darkImageUrl}
              onChange={(variant, url) =>
                variant === 'dark' ? setDarkImageUrl(url) : setImageUrl(url)
              }
            />
          </div>

          {mutation.error && (
            <p className="text-[13px] text-[var(--state-danger)]">
              {(mutation.error as any)?.message ?? 'Something went wrong.'}
            </p>
          )}
        </div>

        {/* Modal footer */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || mutation.isPending}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            {mutation.isPending ? 'Creating...' : 'Create Category'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reject reason modal ───────────────────────────────────────────────────────

function RejectModal({
  title,
  onConfirm,
  onClose,
  isPending,
}: {
  title: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg p-8">
        {/* Modal header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[18px] font-semibold text-[var(--text-primary)] tracking-[-0.01em]">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
            Reason *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this is being rejected..."
            rows={3}
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
          />
        </div>

        {/* Modal footer */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={isPending || !reason.trim()}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[14px] font-medium text-[var(--state-danger)] transition-all duration-200 hover:border-[var(--state-danger)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Rejecting...' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Category row ──────────────────────────────────────────────────────────────

function CategoryRow({
  category,
  childCategories,
}: {
  category: Category;
  childCategories: Category[];
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  const approveMutation = useMutation({
    mutationFn: (id: string) => catalogService.approveCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      catalogService.rejectCategory(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setRejectTarget(null);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => catalogService.archiveCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => catalogService.restoreCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });

  const renderActions = (id: string, status: Category['status'], compact: boolean) => {
    const btnBase = compact
      ? 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium transition-all duration-200 disabled:opacity-50'
      : 'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-all duration-200 disabled:opacity-50';
    // §7.1: the button never moves. `hover:-translate-y-0.5` lifted the body.
    // Danger glows red, not green: a green glow under a Delete button reads as
    // confirmation.
    const primary = `${btnBase} bg-[var(--brand-forest)] text-white font-semibold hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95`;
    const danger = `${btnBase} border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] text-[var(--state-danger)] hover:border-[var(--state-danger)] hover:brightness-110 hover:shadow-[0_12px_32px_rgba(179,38,30,0.32)] active:brightness-95`;
    const neutral = `${btnBase} border border-[var(--border-default)] bg-white text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95`;
    const iconSize = compact ? 'w-3 h-3' : 'w-3 h-3';

    return (
      <PermissionGate permission="categories.approve">
        <div className="flex items-center gap-2">
          {status === 'pending_approval' && (
            <>
              <button
                onClick={() => approveMutation.mutate(id)}
                disabled={approveMutation.isPending}
                className={primary}
              >
                <Check className={iconSize} strokeWidth={1.5} />
                Approve
              </button>
              <button onClick={() => setRejectTarget(id)} className={danger}>
                <X className={iconSize} strokeWidth={1.5} />
                Reject
              </button>
            </>
          )}
          {status === 'approved' && (
            <>
              <button onClick={() => setRejectTarget(id)} className={danger}>
                <X className={iconSize} strokeWidth={1.5} />
                Reject
              </button>
              <button
                onClick={() => archiveMutation.mutate(id)}
                disabled={archiveMutation.isPending}
                className={neutral}
              >
                <Archive className={iconSize} strokeWidth={1.5} />
                Archive
              </button>
            </>
          )}
          {status === 'rejected' && (
            <>
              <button
                onClick={() => restoreMutation.mutate(id)}
                disabled={restoreMutation.isPending}
                className={primary}
              >
                <RotateCcw className={iconSize} strokeWidth={1.5} />
                Restore
              </button>
              <button
                onClick={() => archiveMutation.mutate(id)}
                disabled={archiveMutation.isPending}
                className={neutral}
              >
                <Archive className={iconSize} strokeWidth={1.5} />
                Archive
              </button>
            </>
          )}
          {status === 'archived' && (
            <button
              onClick={() => restoreMutation.mutate(id)}
              disabled={restoreMutation.isPending}
              className={primary}
            >
              <RotateCcw className={iconSize} strokeWidth={1.5} />
              Restore
            </button>
          )}
        </div>
      </PermissionGate>
    );
  };

  return (
    <>
      <div className="flex items-center gap-4 px-6 py-4 hover:bg-[var(--bg-base)] transition-colors">
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-6 h-6 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-0"
          disabled={childCategories.length === 0}
        >
          {childCategories.length > 0 ? (
            expanded ? (
              <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
            ) : (
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
            )
          ) : (
            <span className="w-4 h-4 block" />
          )}
        </button>

        <CategoryIconButton category={category} />

        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-[var(--text-primary)]">{category.name}</div>
          {category.description && (
            <div className="text-[13px] text-[var(--text-muted)] truncate mt-0.5">{category.description}</div>
          )}
          {category.status === 'rejected' && category.rejectionReason && (
            <div className="text-[12px] text-[var(--state-danger)] mt-0.5">
              Reason: {category.rejectionReason}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <StatusBadge status={category.status} />
          {renderActions(category.id, category.status, false)}
        </div>
      </div>

      {/* Child rows */}
      {expanded && childCategories.length > 0 && (
        <div className="ml-10 border-l border-[var(--border-subtle)]">
          {childCategories.map((child) => (
            <div
              key={child.id}
              className="flex items-center gap-4 px-6 py-3 hover:bg-[var(--bg-base)] transition-colors"
            >
              <CategoryIconButton category={child} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[var(--text-primary)]">{child.name}</div>
                {child.description && (
                  <div className="text-[12px] text-[var(--text-muted)] truncate mt-0.5">
                    {child.description}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <StatusBadge status={child.status} />
                {renderActions(child.id, child.status, true)}
              </div>
            </div>
          ))}
        </div>
      )}

      {rejectTarget && (
        <RejectModal
          title="Reject Category"
          onConfirm={(reason) => rejectMutation.mutate({ id: rejectTarget, reason })}
          onClose={() => setRejectTarget(null)}
          isPending={rejectMutation.isPending}
        />
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const CategoriesPage = () => {
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'archived'>('all');

  const {
    data: categories = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['categories'],
    queryFn: () => catalogService.listCategories('all'),
  });

  const filtered = categories.filter((c) => {
    if (c.parentId) return false; // top-level only in main list
    if (filter === 'pending' && c.status !== 'pending_approval') return false;
    if (filter === 'approved' && c.status !== 'approved') return false;
    if (filter === 'rejected' && c.status !== 'rejected') return false;
    if (filter === 'archived' && c.status !== 'archived') return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const pendingCount = categories.filter((c) => c.status === 'pending_approval').length;

  return (
    <PageGuard permission="categories.read">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">
              Catalog
            </p>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)] leading-none">
              Categories
            </h1>
            <p className="text-[14px] text-[var(--text-muted)] mt-1.5">
              Product categories, and approvals for merchant submissions
            </p>
            <p className="text-[13px] text-[var(--text-muted)] mt-1">
              Tap a category&apos;s tile to set the icons shoppers see, for light and
              dark mode. Without one, an icon is drawn from the name.
            </p>
          </div>
          <PermissionGate permission="categories.create">
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 shrink-0 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Add Category
            </button>
          </PermissionGate>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search categories..."
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>

          {/* Status filter pill group */}
          <div className="flex rounded-full border border-[var(--border-subtle)] overflow-hidden bg-white">
            {(['all', 'pending', 'approved', 'rejected', 'archived'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-4 py-2 text-[13px] font-medium transition-colors capitalize',
                  filter === f
                    ? 'bg-[var(--brand-forest)] text-white'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]',
                )}
              >
                {f === 'pending'
                  ? `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}`
                  : f === 'all'
                    ? 'All'
                    : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Category list */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          {/* Table column header */}
          <div className="flex items-center gap-4 px-6 py-3 bg-[var(--bg-base)] border-b border-[var(--border-subtle)]">
            <span className="w-6 shrink-0" />
            <span className="w-4 shrink-0" />
            <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              Category
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] shrink-0">
              Status / Actions
            </span>
          </div>

          {isLoading && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              Loading categories...
            </div>
          )}

          {error && (
            <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
              Failed to load categories.
            </div>
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              {search || filter !== 'all'
                ? 'No categories match your filters.'
                : 'No categories yet.'}
            </div>
          )}

          {!isLoading && !error && filtered.length > 0 && (
            <div className="divide-y divide-[var(--border-subtle)]">
              {filtered.map((cat) => {
                const children = categories.filter((c) => c.parentId === cat.id);
                return (
                  <CategoryRow key={cat.id} category={cat} childCategories={children} />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateCategoryModal
          categories={categories}
          onClose={() => setShowCreate(false)}
        />
      )}
    </PageGuard>
  );
};
